/**
 * DecoratorsBest Typesense Search — InstantSearch.js Integration
 *
 * Replaces Searchspring with Typesense-powered search on:
 *   - /search page
 *   - /collections/* pages
 *
 * Dependencies (loaded via CDN in ts-script.liquid):
 *   - instantsearch.js v4
 *   - typesense-instantsearch-adapter v2
 */

(async function () {
  'use strict';

  // ── Configuration (injected by ts-script.liquid) ──────────────────
  const CONFIG = window.__TYPESENSE_CONFIG || {};
  if (!CONFIG.apiKey || !CONFIG.host) {
    console.warn('[Typesense Search] Missing configuration. Aborting.');
    return;
  }

  const isTrade = CONFIG.isTrade === true;
  const moneyFormat = CONFIG.moneyFormat || '${{amount}}';
  const collectionName = CONFIG.collectionName || 'products';
  const pageType = CONFIG.pageType || '';
  const collectionHandle = CONFIG.collectionHandle || '';

  // ── Search Config (loaded from admin dashboard) ───────────────────
  // Fetches live config from the admin backend so merchandisers can adjust
  // weights, typo tolerance, and field order without a theme deploy.
  //
  // Hits the admin server CROSS-ORIGIN via settings.ts_admin_host (same
  // mechanism as /search-log). A relative-path fetch only works if the
  // storefront CDN proxies /search-config to the admin host, which ours
  // doesn't — that's why dashboard edits silently never reached the
  // storefront until this change. Falls back to hardcoded defaults if
  // adminHost is unset OR the cross-origin fetch fails for any reason.
  //
  // Defaults below MUST stay in sync with config_store.DEFAULTS on the
  // backend — they're the safety net if the network round-trip fails.
  const _SEARCH_DEFAULTS = {
    // collection_titles intentionally NOT searched — it matched every generic
    // collection a product sits in ("Beige Wallpaper", "All Kravet Wallpaper"),
    // so "wallpaper" pulled in 68k fabrics. See config_store.DEFAULTS.
    query_by:         'title,sku,mpn,description,vendor,tag_brand,tag_designer,tag_type,tag_color,tag_size,tag_pattern,tag_style,tag_category,tags_raw',
    query_by_weights: '10,8,8,1,4,6,6,5,4,4,4,3,3,1',
    num_typos:        '2,0,0,0,1,1,1,1,1,1,1,1,1,0',
    sort_by:          'popularity:desc,_text_match:desc',
    typo_tokens_threshold: 3,
    // drop_tokens_threshold=0 forces strict AND across all query tokens —
    // every word must match somewhere. Old value 2 silently dropped tokens
    // ("a street springhill" → just "a street" → 2,400 vendor catalog
    // matches). Per Barbara 5/27. Keep aligned with config_store.DEFAULTS.
    drop_tokens_threshold: 0,
  };
  let _searchConfig = Object.assign({}, _SEARCH_DEFAULTS);
  try {
    const _cfgAdminHost = (CONFIG.adminHost || '').replace(/\/+$/, '');
    const _cfgUrl = _cfgAdminHost
      ? 'https://' + _cfgAdminHost + '/search-config'
      : '/search-config';   // legacy same-origin path; only works if a CDN rule proxies it
    const _cfgResp = await fetch(_cfgUrl, { cache: 'default', mode: 'cors' }).catch(() => null);
    if (_cfgResp && _cfgResp.ok) {
      const _cfgData = await _cfgResp.json().catch(() => null);
      if (_cfgData) _searchConfig = Object.assign({}, _SEARCH_DEFAULTS, _cfgData);
    }
  } catch (_) { /* fall back to defaults */ }

  // ── Adapter Setup ─────────────────────────────────────────────────
  const typesenseAdapter = new TypesenseInstantSearchAdapter({
    server: {
      apiKey: CONFIG.apiKey,
      nodes: [{
        host: CONFIG.host,
        port: CONFIG.port || '443',
        protocol: CONFIG.protocol || 'https',
      }],
      // Short cache window. Browsing behaviour and re-running the same
      // query inside a few seconds (refining, paginating back) benefits
      // from caching, but anything longer risks serving stale results
      // after a webhook update or a popularity refresh.
      cacheSearchResultsForSeconds: 30,
    },
    // Preserve grouped_hits so we can render size/color families as a single
    // card with a price range. Without this the adapter flattens groups back
    // into individual hits.
    flattenGroupedHits: false,
    additionalSearchParameters: {
      // `collections` is a slug list (e.g. "wallpaper-by-style"); searching
      // it polluted relevance with handle-fragment matches. Tag fields
      // already cover brand/designer/type/etc.
      query_by: _searchConfig.query_by,
      query_by_weights: _searchConfig.query_by_weights,
      highlight_full_fields: 'title',
      num_typos: _searchConfig.num_typos,
      // SKUs and MPNs are alphanumeric identifiers — typo tolerance turns
      // "DBSP100" into a fuzzy match for unrelated SKUs and is worse than
      // returning zero results.
      enable_typos_for_alpha_numerical_tokens: false,
      typo_tokens_threshold: _searchConfig.typo_tokens_threshold,
      drop_tokens_threshold: _searchConfig.drop_tokens_threshold,
      // Collapse size/color sibling products into one card.
      // group_id is derived from custom.related_products at sync time and
      // falls back to the product handle, so unrelated items remain ungrouped.
      group_by: 'group_id',
      group_limit: 50,
      group_missing_values: true,
    },
    collectionSpecificSearchParameters: {},
  });

  const rawSearchClient = typesenseAdapter.searchClient;

  // ── Query-token → filter promotion (A2 / B2) ─────────────────────
  // When the shopper types tokens that match a known brand, type,
  // category, or designer (e.g. "york wallpaper", "schumacher"), promote
  // those tokens to hard filter_by clauses and strip them from the text
  // query. This keeps Searchspring-like behaviour where typing a brand
  // name returns ONLY that brand instead of relevance-ranked stragglers.
  //
  // The dictionary of facet values is built lazily from facet counts in
  // each search response (we already get them back for the sidebar) and
  // cached in localStorage with a 24h TTL so subsequent page loads have
  // hard-matching available immediately. Per OWASP A03, all dictionary
  // entries are echoed back from Typesense (which validated them on
  // ingest), so we don't need to sanitize beyond escaping backticks.
  const PROMOTABLE_FIELDS = ['tag_brand', 'tag_type', 'tag_category', 'tag_designer'];
  const FACET_DICT_KEY = 'ts_facet_dictionary_v1';
  const FACET_DICT_TTL_MS = 24 * 60 * 60 * 1000;
  const knownFacetValues = {};
  PROMOTABLE_FIELDS.forEach((f) => { knownFacetValues[f] = new Map(); });

  try {
    const cached = JSON.parse(localStorage.getItem(FACET_DICT_KEY) || 'null');
    if (cached && cached.t && Date.now() - cached.t < FACET_DICT_TTL_MS && cached.d) {
      PROMOTABLE_FIELDS.forEach((field) => {
        const entries = cached.d[field];
        if (!Array.isArray(entries)) return;
        entries.forEach((pair) => {
          if (Array.isArray(pair) && pair.length === 2) {
            knownFacetValues[field].set(pair[0], pair[1]);
          }
        });
      });
    }
  } catch (_) { /* localStorage disabled, fall back to in-memory only */ }

  let _persistTimer = null;
  let _lastOriginalQuery = '';   // tracks the pre-promotion query for stats display
  function persistFacetDictionary() {
    if (_persistTimer) return;
    _persistTimer = setTimeout(() => {
      _persistTimer = null;
      try {
        const out = { t: Date.now(), d: {} };
        PROMOTABLE_FIELDS.forEach((field) => {
          out.d[field] = Array.from(knownFacetValues[field].entries());
        });
        localStorage.setItem(FACET_DICT_KEY, JSON.stringify(out));
      } catch (_) { /* quota exceeded or disabled */ }
    }, 1000);
  }

  function ingestFacetCounts(response) {
    if (!response || !Array.isArray(response.results)) return;
    let changed = false;
    response.results.forEach((r) => {
      if (!r || !r.facets) return;
      PROMOTABLE_FIELDS.forEach((field) => {
        const facet = r.facets[field];
        if (!facet) return;
        Object.keys(facet).forEach((value) => {
          // Skip values that look like noise (numbers only, single chars)
          // since a token like "12" or "a" in a query shouldn't pin a brand.
          if (value.length < 2 || /^\d+$/.test(value)) return;
          const lo = value.toLowerCase();
          if (!knownFacetValues[field].has(lo)) {
            knownFacetValues[field].set(lo, value);
            changed = true;
          }
        });
      });
    });
    if (changed) persistFacetDictionary();
  }

  // The dictionary only learned values from earlier results, so a shopper's
  // first search never matched a brand. One facet-only request fills it with
  // every brand, type, category and designer, at most once a day (it's kept
  // in localStorage); the page doesn't wait more than 1.5 s for it.
  async function seedFacetDictionary() {
    if (knownFacetValues.tag_brand.size > 50) return;
    try {
      const response = await rawSearchClient.search([{
        indexName: collectionName,
        params: { query: '', facets: PROMOTABLE_FIELDS, maxValuesPerFacet: 1000, hitsPerPage: 0 },
      }]);
      ingestFacetCounts(response);
    } catch (_) { /* search still works, it just learns as it goes */ }
  }
  await Promise.race([seedFacetDictionary(), new Promise((resolve) => setTimeout(resolve, 1500))]);

  // Generate candidate singular forms of a phrase using standard English
  // plural suffix rules. Applied in order from most-specific to least.
  // This means plurals like "accessories" → "accessory", "benches" → "bench",
  // "shelves" → "shelf", "beds" → "bed" all work without any manual mapping.
  function _depluralCandidates(phrase) {
    const s = phrase;
    // Multi-word phrases: only the last word is typically pluralised,
    // e.g. "dining tables" → "dining table", "floor lamps" → "floor lamp".
    const spaceIdx = s.lastIndexOf(' ');
    const prefix = spaceIdx >= 0 ? s.slice(0, spaceIdx + 1) : '';
    const word   = spaceIdx >= 0 ? s.slice(spaceIdx + 1)    : s;
    const out = [];
    if (/ies$/.test(word))      out.push(prefix + word.replace(/ies$/, 'y'));   // accessories → accessory
    if (/ves$/.test(word)) {
      out.push(prefix + word.replace(/ves$/, 'f'));                              // shelves → shelf
      out.push(prefix + word.replace(/ves$/, 'fe'));                             // knives → knife
    }
    if (/ches$/.test(word))     out.push(prefix + word.replace(/ches$/, 'ch')); // benches → bench
    if (/shes$/.test(word))     out.push(prefix + word.replace(/shes$/, 'sh')); // brushes → brush
    if (/xes$/.test(word))      out.push(prefix + word.replace(/xes$/, 'x'));   // boxes → box
    if (/ses$/.test(word))      out.push(prefix + word.replace(/ses$/, 's'));   // grasses → grass
    if (/s$/.test(word) && word.length > 2) out.push(prefix + word.replace(/s$/, '')); // beds → bed
    return out;
  }

  // Look up a phrase in a field's dictionary, trying plural→singular transforms
  // when an exact match fails. "and" and "&" are the same word: shoppers type
  // "villa and house" for the brand "Villa & House", and without this "and"
  // had to appear in the product text (57 products instead of 496, Barbara
  // 11/3). Returns the canonical facet value or null.
  function _lookupFacetValue(field, phrase) {
    const map = knownFacetValues[field];
    const spellings = [phrase, phrase.replace(/\band\b/g, '&'), phrase.replace(/\s*&\s*/g, ' and ')];
    for (const spelling of spellings) {
      if (map.has(spelling)) return map.get(spelling);
      for (const candidate of _depluralCandidates(spelling)) {
        if (map.has(candidate)) return map.get(candidate);
      }
    }
    return null;
  }

  // Tokenize on whitespace; keep alphanumerics together. Multi-word brands
  // (e.g. "Ralph Lauren") matter, so we test phrases up to 4 tokens.
  const MAX_PHRASE_LEN = 4;
  function promoteQueryToFilters(query) {
    if (!query || typeof query !== 'string') return null;
    const tokens = query.trim().split(/\s+/).filter(Boolean);
    if (!tokens.length) return null;
    const used = new Array(tokens.length).fill(false);
    const matches = [];
    // For each promotable field we accept at most one match (a query
    // saying "york schumacher" shouldn't filter to BOTH brands which
    // would zero-out results). Longest-phrase-first per field.
    PROMOTABLE_FIELDS.forEach((field) => {
      for (let len = Math.min(MAX_PHRASE_LEN, tokens.length); len >= 1; len--) {
        let foundForLen = false;
        for (let start = 0; start + len <= tokens.length; start++) {
          let overlap = false;
          for (let i = start; i < start + len; i++) {
            if (used[i]) { overlap = true; break; }
          }
          if (overlap) continue;
          const phrase = tokens.slice(start, start + len).join(' ').toLowerCase();
          const canonical = _lookupFacetValue(field, phrase);
          if (canonical !== null) {
            matches.push({ field, value: canonical });
            for (let i = start; i < start + len; i++) used[i] = true;
            foundForLen = true;
            break;
          }
        }
        if (foundForLen) break;
      }
    });
    if (!matches.length) return null;
    const remaining = tokens.filter((_, i) => !used[i]).join(' ');
    return { matches, remainingQuery: remaining };
  }

  function buildPromotedFilterClause(matches) {
    // Typesense filter_by syntax with backticks for exact match. We
    // escape backticks defensively even though indexed values shouldn't
    // contain them.
    return matches
      .map((m) => `${m.field}:=\`${String(m.value).replace(/`/g, '')}\``)
      .join(' && ');
  }

  // ── Type-first ranking ────────────────────────────────────────────
  // Best Selling ranks by popularity alone, so "dining chairs" opened with
  // popular fabrics whose description says "great for dining chairs". When
  // the query names a product type from /search-config's type_first list
  // (furniture, lighting, pillows, decor, wall art, bedding, mirrors, rugs;
  // never fabric or wallpaper), that type ranks first and best sellers still
  // lead within each group (Barbara, 10/27). A word that ends two or more
  // types covers them all: "tables" = coffee, dining, side... tables.
  const typeFirstExact = new Map();   // 'dining chair' → 'Dining Chair'
  const typeFirstByEnd = new Map();   // 'table' → ['Accent Table', 'Coffee Table', ...]
  (Array.isArray(_searchConfig.type_first) ? _searchConfig.type_first : []).forEach((value) => {
    if (typeof value !== 'string' || !value) return;
    const lo = value.toLowerCase();
    typeFirstExact.set(lo, value);
    const end = lo.slice(lo.lastIndexOf(' ') + 1);
    if (end !== lo) typeFirstByEnd.set(end, (typeFirstByEnd.get(end) || []).concat(value));
  });

  function _typeFirstValues(phrase) {
    for (const candidate of [phrase].concat(_depluralCandidates(phrase))) {
      const exact = typeFirstExact.get(candidate) || null;
      const byEnd = typeFirstByEnd.get(candidate) || [];
      const values = (exact ? [exact] : []).concat(byEnd.length >= 2 ? byEnd : []);
      if (values.length) return { values, exact };
    }
    return null;
  }

  // Typesense sort_by that puts the types named in `query` first, or null.
  function typeFirstSortBy(query) {
    if (!query || typeof query !== 'string' || !typeFirstExact.size) return null;
    const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const used = new Array(tokens.length).fill(false);
    const values = new Set();
    const titleClauses = [];
    for (let len = Math.min(MAX_PHRASE_LEN, tokens.length); len >= 1; len--) {
      for (let start = 0; start + len <= tokens.length; start++) {
        if (used.slice(start, start + len).some(Boolean)) continue;
        const hit = _typeFirstValues(tokens.slice(start, start + len).join(' '));
        if (!hit) continue;
        hit.values.forEach((v) => values.add(v));
        // Products missing their type tags still count when the title names
        // the type ("... Dining Chairs, Set of 2").
        if (hit.exact && /^[A-Za-z0-9 ]+$/.test(hit.exact)) {
          const words = hit.exact.toLowerCase().split(' ');
          const last = words.pop().replace(/y$/, '');
          titleClauses.push('(' + words.map((w) => `title:${w}`).concat(`title:${last}*`).join(' && ') + ')');
        }
        for (let i = start; i < start + len; i++) used[i] = true;
      }
    }
    if (!values.size) return null;
    const list = '[' + Array.from(values, (v) => '`' + v.replace(/`/g, '') + '`').join(',') + ']';
    const named = [`tag_type:=${list}`, `tag_subtype:=${list}`].concat(titleClauses).join(' || ');
    return `_eval(${named}):desc,popularity:desc,_text_match:desc`;
  }

  // Swap Best Selling for the type-first sort when the query names a type.
  function withTypeFirstSort(req) {
    if (req.indexName !== collectionName + '/sort/popularity:desc') return req;
    const sortBy = typeFirstSortBy(req.params.query);
    return sortBy ? { ...req, indexName: collectionName + '/sort/' + sortBy } : req;
  }

  const searchClient = {
    ...rawSearchClient,
    search(requests) {
      const mutated = (requests || []).map((req) => {
        if (!req || !req.params) return req;
        _lastOriginalQuery = req.params.query || '';
        const promoted = promoteQueryToFilters(req.params.query);
        if (!promoted) return withTypeFirstSort(req);
        const promotedFilters = buildPromotedFilterClause(promoted.matches);
        const newParams = { ...req.params, query: promoted.remainingQuery };
        if (newParams.filters) {
          newParams.filters = '(' + newParams.filters + ') && ' + promotedFilters;
        } else {
          newParams.filters = promotedFilters;
        }
        return withTypeFirstSort({ ...req, params: newParams });
      });
      return rawSearchClient.search(mutated).then((response) => {
        try { ingestFacetCounts(response); } catch (_) { /* never break search */ }
        return response;
      });
    },
  };
  if (typeof rawSearchClient.searchForFacetValues === 'function') {
    searchClient.searchForFacetValues = rawSearchClient.searchForFacetValues.bind(rawSearchClient);
  }

  // ── Helpers ───────────────────────────────────────────────────────
  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    const amount = parseFloat(cents).toFixed(2);
    const amountNoDecimals = parseInt(cents, 10).toString();
    const amountWithCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return moneyFormat
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amountWithCommas);
  }

  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // "New" badge — render a label when the product carries a tag matching
  // `settings.new_label_tag` (default "New", case-insensitive). Mirrors
  // snippets/product-label.liquid's logic but on the Typesense-driven PLP,
  // which doesn't go through the Liquid product-card snippet.
  //
  // Uses the theme's existing .product-label / .product-label--new CSS
  // classes so the badge picks up the merchant-configured background/text
  // colors automatically (--new-label-bg-color / --new-label-text-color).
  // Positioning matches the Liquid snippet — top-left of the card image.
  //
  // Days-based fallback (created_at within N days) uses the indexed
  // `published_at` field. Collection-based fallback isn't supported on the
  // Typesense PLP — would need every product's collection memberships
  // re-checked client-side against the configured collection list.
  function renderNewBadge(hit) {
    if (!hit) return '';
    // DecoratorsBest tags the "new" flag as `Group:New` in Shopify, which
    // transform.py routes to the indexed `tag_group` field (not tags_raw).
    // Check tag_group first; fall back to tags_raw for products that
    // happen to carry a plain "New" tag instead.
    var pools = [hit.tag_group, hit.tags_raw];
    var hasNew = false;
    for (var p = 0; p < pools.length; p++) {
      var arr = pools[p] || [];
      for (var i = 0; i < arr.length; i++) {
        if (String(arr[i] || '').toLowerCase() === 'new') {
          hasNew = true;
          break;
        }
      }
      if (hasNew) break;
    }
    if (!hasNew) return '';
    return '<div class="product-label-container absolute" style="top:8px;left:8px">' +
             '<div class="product-label-wrapper flex">' +
               '<span class="product-label product-label--new">New</span>' +
             '</div>' +
           '</div>';
  }

  function getProductUrl(hit) {
    return '/products/' + hit.handle;
  }

  // isTrade == B2B company membership (the legacy trade tag is retired).
  // trade_price comes from the custom.trade_price metafield mirror, and the
  // trade add-to-cart id is the Default variant (the B2B catalog reprices it
  // at cart). Consumer fallbacks keep old index documents rendering during
  // reindex windows.
  function getPrice(hit) {
    if (isTrade) {
      return hit.trade_price != null ? hit.trade_price : hit.consumer_price;
    }
    return hit.consumer_price;
  }

  // Trade cards strike through the consumer price, like the product page
  // (snippets/price.liquid). Never the consumer site's sale compare-at: the
  // Furniture Classics price-drop "was" prices aren't a trade sale (Barbara,
  // 2026-10-20).
  function getCompareAtPrice(hit) {
    if (isTrade) {
      return hit.consumer_price;
    }
    return hit.consumer_compare_at_price;
  }

  function getVariantId(hit) {
    if (isTrade) {
      return hit.trade_variant_id || hit.consumer_variant_id;
    }
    return hit.consumer_variant_id;
  }

  function sanitizeHighlight(snippet) {
    return snippet.replace(/<(?!\/?(mark)\b)[^>]*>/gi, '');
  }

  // Strip size strings (e.g. "5'1\" x 7'") out of a product title so that a
  // grouped card showing many sizes doesn't display one specific size.
  // Title sanitiser used for grouped cards. Collects every variant token
  // that differs across siblings (colors, sizes, patterns) and strips them
  // from the displayed title so a grouped Lilly Pulitzer card no longer
  // reads "Lee Jofa Surf Stripe Palm Green Upholstery Fabric" but rather
  // "Lee Jofa Surf Stripe Upholstery Fabric". The variant info is then
  // surfaced separately via swatches or a count hint underneath the title.
  function stripVariantTokensFromTitle(title, tokens) {
    if (!title || !tokens || !tokens.length) return title;
    let result = title;
    tokens.forEach(function (token) {
      if (!token) return;
      const escaped = String(token).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Surrounding whitespace bracketing handles both "X Foo Y" and "Foo Y" /
      // "X Foo" cases; case-insensitive so "Palm Green" and "palm green" both
      // strip. Multi-word tokens like "California King" work because the
      // escape only touches regex metachars, not whitespace.
      result = result.replace(new RegExp('\\s*' + escaped + '\\s*', 'gi'), ' ');
    });
    return result.replace(/\s+/g, ' ').trim();
  }

  // Detect what dimension the group varies along (color / size / pattern /
  // generic). Used by both InstantSearch cards and predictive cards to
  // decide whether to render image swatches or a text hint.
  function detectGroupingDimension(siblings) {
    if (!siblings || siblings.length < 2) return { kind: 'none', count: 1 };
    var colors = new Set(), sizes = new Set(), patterns = new Set();
    siblings.forEach(function (s) {
      (s.tag_color   || []).forEach(function (v) { if (v) colors.add(v); });
      (s.tag_size    || []).forEach(function (v) { if (v) sizes.add(v); });
      (s.tag_pattern || []).forEach(function (v) { if (v) patterns.add(v); });
    });
    // Pick the dimension with the most variation. Ties go color > size >
    // pattern because color is the most common storefront grouping case.
    if (colors.size > 1 && colors.size >= sizes.size && colors.size >= patterns.size) {
      return { kind: 'color', count: colors.size };
    }
    if (sizes.size > 1 && sizes.size >= patterns.size) {
      return { kind: 'size', count: sizes.size };
    }
    if (patterns.size > 1) {
      return { kind: 'pattern', count: patterns.size };
    }
    return { kind: 'generic', count: siblings.length };
  }

  // Collect every variant token across the sibling set — used as the strip
  // list for stripVariantTokensFromTitle.
  function collectVariantTokens(siblings) {
    var out = new Set();
    siblings.forEach(function (s) {
      (s.tag_color   || []).forEach(function (v) { if (v) out.add(v); });
      (s.tag_size    || []).forEach(function (v) { if (v) out.add(v); });
      (s.tag_pattern || []).forEach(function (v) { if (v) out.add(v); });
    });
    return Array.from(out);
  }

  // Render the variant indicator under the title. Color groups show small
  // rounded thumbnails (one per sibling, capped at 5); size/pattern groups
  // show a text hint ("4 sizes available"). For thumbnail rendering we use
  // each sibling's image_url so the swatches actually preview the variants.
  function renderVariantIndicator(siblings) {
    var dim = detectGroupingDimension(siblings);
    if (dim.kind === 'none' || dim.kind === 'generic' && siblings.length < 2) return '';
    // Color swatches are no longer derived from grouped siblings — color
    // variants now render as their own separate cards. The swatch row comes
    // from related_products_json via renderColorSwatches() instead. A
    // color-varying group shouldn't occur post-regroup; guard against one
    // rendering a misleading size/pattern hint.
    if (dim.kind === 'color') return '';
    var label = dim.kind === 'size'    ? (dim.count + ' sizes available')
              : dim.kind === 'pattern' ? (dim.count + ' patterns available')
              :                          (dim.count + ' variants');
    return '<p class="ts-card__variant-hint">' + label + '</p>';
  }

  // Parse the indexed related_products_json (distinct colorways of this
  // product's family) into an array of {handle, color, image_url}.
  function parseColorSiblings(hit) {
    var raw = hit && hit.related_products_json;
    if (!raw) return [];
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw); } catch (e) { return []; }
    }
    return Array.isArray(raw) ? raw : [];
  }

  // Render the color-swatch row from related_products_json. Each colorway is
  // its own card now; this row signals the other colors the design comes in.
  // Only renders when at least two colorways have images (one visible swatch
  // isn't worth a row). Sibling images are baked in at rebuild time, so a
  // freshly webhook-updated product may briefly have none until the next
  // rebuild — in which case the row is simply omitted.
  function renderColorSwatches(hit) {
    var list = parseColorSiblings(hit).filter(function (s) { return s && s.image_url; });
    if (list.length < 2) return '';
    var capped = list.slice(0, 5);
    var swatches = capped.map(function (s) {
      var url = String(s.image_url).replace(/\?.*$/, '') + '?width=72&quality=80';
      return '<span class="ts-card__swatch" style="background-image:url(' + url + ')" title="' +
             escapeHtml(s.color || '') + '"></span>';
    }).join('');
    var more = list.length > capped.length
      ? '<span class="ts-card__swatch-more">+' + (list.length - capped.length) + '</span>'
      : '';
    return '<div class="ts-card__swatches" aria-label="' + list.length + ' colors available">' + swatches + more + '</div>';
  }

  // "See This On My ..." buttons are injected by the ideal.house SDK itself.
  // The SDK (loaded async in layout/theme.liquid) watches the DOM via
  // MutationObserver for elements with class `grid-view-item`. When it finds
  // one, it traverses up to the parent, resolves the product handle from the
  // nearest <a> href, checks ideal.house's catalog API, and injects + shows
  // its own button if the product is in their database. We just need the
  // `grid-view-item` class on the right container (see card template below).

  function normalizeRefinementValues(value) {
    if (typeof value !== 'string') return [];
    return value
      .split('~')
      .map(function (part) {
        return part.trim();
      })
      .filter(Boolean)
      .filter(function (part, index, all) {
        return all.indexOf(part) === index;
      });
  }

  function normalizeRangeValue(value) {
    if (typeof value !== 'string') return null;
    var parts = value
      .split(/[~:]/)
      .map(function (part) {
        return part.trim();
      })
      .filter(Boolean);
    if (parts.length < 2) return null;
    return parts[0] + ':' + parts[1];
  }

  function normalizeToggleValue(value) {
    if (value === true) return true;
    if (typeof value !== 'string') return false;
    return /^(1|true|yes|on)$/i.test(value.trim());
  }

  // ── Product Card Renderer ─────────────────────────────────────────
  // Sale-code note on product cards (Barbara 2026-10-05): on a sale page,
  // fabric and wallpaper keep their normal price, with a reminder of the code
  // under it. Retail only (trade accounts can't use sale codes), and only on
  // the days the code works, in the store's time zone.
  const CARD_CODE_NOTES = {
    'prime-sale': {
      types: ['Fabric', 'Wallpaper'],
      text: '15% off with code PRIME26',
      from: '2026-10-06',
      until: '2026-10-07',
    },
  };

  function renderCodeNote(hit) {
    const note = pageType === 'collection' && !isTrade && CARD_CODE_NOTES[collectionHandle];
    if (!note) return '';
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    if (today < note.from || today > note.until) return '';
    const types = hit.tag_type || [];
    if (!note.types.some(function (type) { return types.indexOf(type) !== -1; })) return '';
    return '<p class="ts-card__code-note text-sm font-bold mt-1 mb-0">' + escapeHtml(note.text) + '</p>';
  }

  function renderProductCard(hit) {
    // When `group_by` is active and `flattenGroupedHits: false`, the adapter
    // sets the group's first hit as the primary `hit` and exposes every
    // sibling in `_grouped_hits`. We use the siblings to derive a price
    // range and to strip per-size suffixes out of the title.
    const groupedHits = Array.isArray(hit._grouped_hits) ? hit._grouped_hits : [];
    const isGrouped = groupedHits.length > 1;

    const url = getProductUrl(hit);
    const imageUrl = hit.image_url
      ? hit.image_url.replace(/\?.*$/, '') + '?width=460&quality=80'
      : '';

    // Aggregate every variant token (color/size/pattern) across the sibling
    // set so the displayed title can drop them — the variants are surfaced
    // separately below via swatches or a count hint, see renderVariantIndicator.
    const variantTokens = isGrouped ? collectVariantTokens(groupedHits) : [];

    const rawTitle = hit._highlightResult && hit._highlightResult.title
      ? sanitizeHighlight(hit._highlightResult.title.value)
      : escapeHtml(hit.title);
    const title = isGrouped ? stripVariantTokensFromTitle(rawTitle, variantTokens) : rawTitle;
    const variantIndicatorHtml = isGrouped ? renderVariantIndicator(groupedHits) : '';
    // Color swatches come from related_products_json (independent of grouping),
    // so even an ungrouped single-color card still shows its sibling colorways.
    const colorSwatchHtml = renderColorSwatches(hit);

    // Price range across the group, otherwise the single hit's price.
    let priceHtml = '';
    let anyOnSale = false;
    if (isGrouped) {
      let min = Infinity;
      let max = -Infinity;
      let cmpMin = Infinity;
      let cmpMax = -Infinity;
      groupedHits.forEach(function (g) {
        const p = getPrice(g);
        if (p == null || isNaN(p)) return;
        if (p < min) min = p;
        if (p > max) max = p;
        const cmp = getCompareAtPrice(g);
        if (cmp && cmp > p) {
          anyOnSale = true;
          if (cmp < cmpMin) cmpMin = cmp;
          if (cmp > cmpMax) cmpMax = cmp;
        }
      });
      if (isFinite(min) && isFinite(max)) {
        const saleClass = anyOnSale ? ' price-item--sale' : '';
        const priceRangeHtml = (min === max)
          ? '<span class="price-item' + saleClass + '">' + formatMoney(min) + '</span>'
          : '<span class="price-item' + saleClass + '">' + formatMoney(min) + '</span>' +
            '<span class="price-item__separator"> &ndash; </span>' +
            '<span class="price-item' + saleClass + '">' + formatMoney(max) + '</span>';
        if (anyOnSale && isFinite(cmpMin) && isFinite(cmpMax)) {
          const cmpRangeText = (cmpMin === cmpMax)
            ? formatMoney(cmpMin)
            : formatMoney(cmpMin) + ' &ndash; ' + formatMoney(cmpMax);
          priceHtml =
            '<span class="price__sale">' +
              priceRangeHtml +
              '<s class="price-item price-item--regular text-theme-light">' + cmpRangeText + '</s>' +
            '</span>';
        } else {
          priceHtml = priceRangeHtml;
        }
      }
    } else {
      const price = getPrice(hit);
      const compareAt = getCompareAtPrice(hit);
      anyOnSale = !!(compareAt && compareAt > price);
      if (price != null) {
        if (anyOnSale) {
          priceHtml =
            '<span class="price__sale">' +
              '<span class="price-item price-item--sale">' + formatMoney(price) + '</span>' +
              '<s class="price-item price-item--regular text-theme-light">' + formatMoney(compareAt) + '</s>' +
            '</span>';
        } else {
          priceHtml = '<span class="price-item">' + formatMoney(price) + '</span>';
        }
      }
    }
    const priceOnSaleClass = anyOnSale ? ' price--on-sale' : '';

    let sampleHtml = '';

    let vendorHtml = '';
    if (hit.vendor) {
      vendorHtml = '<p class="card__vendor mb-0 text-sm text-theme-light">' + escapeHtml(hit.vendor) + '</p>';
    }

    const groupedClass = isGrouped ? ' ts-card--grouped' : '';

    return (
      '<li class="ts-card' + groupedClass + '">' +
        '<div class="card card--product h-full relative flex">' +
          // grid-view-item: required by the ideal.house SDK. Its MutationObserver
          // looks for this class; when found it traverses up one level
          // (.parentElement = div.card--product) and calls querySelector("a")
          // to resolve the product handle, then injects its own button.
          '<div class="card__media relative grid-view-item">' +
            renderNewBadge(hit) +
            '<a href="' + url + '" class="media block relative" tabindex="-1" style="padding-top: 100%;">' +
              (imageUrl
                ? '<img src="' + imageUrl + '" alt="' + escapeHtml(hit.title) + '" class="img-fit card__main-image" loading="lazy" width="460" height="460">'
                : '<svg class="media__placeholder img-fit" viewBox="0 0 525 525"><rect width="525" height="525" fill="#f5f5f5"/></svg>') +
            '</a>' +
          '</div>' +
          '<div class="card__info-container flex flex-col flex-auto relative">' +
            '<div class="card__info w-full">' +
              '<div class="card__info-inner w-full">' +
                vendorHtml +
                '<p class="card__title font-bold mt-1 mb-0">' +
                  '<a href="' + url + '" class="card-link text-current">' + title + '</a>' +
                '</p><div class="star_container ' + hit.id + '"></div>' +
                colorSwatchHtml +
                variantIndicatorHtml +
                '<div class="price mt-1' + priceOnSaleClass + '">' + priceHtml + '</div>' +
                renderCodeNote(hit) +
                sampleHtml +
              '</div>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</li>'
    );
  }

  // ── InstantSearch Instance ────────────────────────────────────────
  const sidebarEl = document.getElementById('typesense-sidebar');
  const contentEl = document.getElementById('typesense-content');
  const priceAttr = isTrade ? 'trade_price' : 'consumer_price';

  if (!contentEl) {
    console.warn('[Typesense Search] No #typesense-content element found.');
    return;
  }

  // Both collection pages and /search default to `popularity:desc` (Best
  // Selling). Collection pages have no query, so `_text_match` is constant and
  // meaningless there; on /search we still lead with best-sellers so shoppers
  // see proven products first, and can switch to Best Match if they want the
  // results ranked purely by relevance to what they typed.
  const defaultSortIndex = collectionName + '/sort/popularity:desc';

  const search = instantsearch({
    indexName: collectionName,
    searchClient: searchClient,
    routing: {
      stateMapping: {
        stateToRoute(uiState) {
          const indexState = uiState[collectionName] || {};
          const routeState = {
            q: indexState.query,
            page: indexState.page,
            sort: (indexState.sortBy && indexState.sortBy !== defaultSortIndex) ? indexState.sortBy : undefined,
          };

          Object.keys(indexState.refinementList || {}).forEach((key) => {
            routeState[key] = indexState.refinementList[key].join('~');
          });

          const rangeState = indexState.range || {};
          const activeRange = rangeState[priceAttr];
          if (typeof activeRange === 'string' && activeRange) {
            const parts = activeRange.split(':');
            if (parts.length >= 2 && parts[0] !== '' && parts[1] !== '') {
              routeState[priceAttr] = parts[0] + '~' + parts[1];
            }
          }

          const toggleState = indexState.toggle || indexState.toggleRefinement || {};
          if (normalizeToggleValue(toggleState.on_sale)) {
            routeState.on_sale = 'true';
          }

          return routeState;
        },
        routeToState(routeState) {
          const refinementList = {};
          const range = {};
          const toggle = {};
          const reservedKeys = ['q', 'page', 'sort'];
          // Allowlist of known facet URL params. Any other param (UTM tags,
          // Shopify tracking params, etc.) is ignored so unknown keys don't
          // silently attempt a facet filter and return 0 results.
          const knownFacets = new Set([
            'tag_brand', 'tag_designer', 'tag_type', 'tag_subtype',
            'tag_category', 'tag_construction', 'tag_content', 'tag_style',
            'tag_color', 'tag_pattern', 'tag_group', 'tag_room', 'tag_shape',
            'tag_size', 'tag_height', 'tag_width',
            'type_hierarchy.lvl0', 'type_hierarchy.lvl1',
            'collections', 'in_stock',
          ]);
          Object.keys(routeState).forEach((key) => {
            if (reservedKeys.includes(key) || !routeState[key] || typeof routeState[key] !== 'string') {
              return;
            }

            if (key === 'on_sale') {
              if (normalizeToggleValue(routeState[key])) {
                toggle.on_sale = true;
              }
              return;
            }

            if (key === priceAttr || key === 'consumer_price' || key === 'trade_price') {
              const normalizedRange = normalizeRangeValue(routeState[key]);
              if (normalizedRange) {
                range[priceAttr] = normalizedRange;
              }
              return;
            }

            if (knownFacets.has(key)) {
              const values = normalizeRefinementValues(routeState[key]);
              if (values.length) {
                refinementList[key] = values;
              }
            }
          });
          return {
            [collectionName]: {
              query: routeState.q,
              page: routeState.page,
              sortBy: routeState.sort || defaultSortIndex,
              refinementList: refinementList,
              range: range,
              toggle: toggle,
            },
          };
        },
      },
    },
    future: { preserveSharedStateOnUnmount: true },
  });

  // ── Widgets ───────────────────────────────────────────────────────
  const widgets = [];

  // Search box (only on search page, not collection pages)
  if (document.getElementById('typesense-searchbox')) {
    widgets.push(
      instantsearch.widgets.searchBox({
        container: '#typesense-searchbox',
      placeholder: 'Search for wallpaper, fabric, furniture...',
      showReset: true,
      showSubmit: true,
      showLoadingIndicator: true,
        cssClasses: {
          root: 'ts-searchbox',
          form: 'ts-searchbox__form',
          input: 'ts-searchbox__input',
          submit: 'ts-searchbox__submit',
          reset: 'ts-searchbox__reset',
        },
      })
    );
  }

  // Stats
  widgets.push(
    instantsearch.widgets.stats({
      container: '#typesense-stats',
      cssClasses: { root: 'ts-stats text-sm text-theme-light mb-4' },
      templates: {
        text(data) {
          if (pageType === 'collection') {
            return `Showing ${data.nbHits.toLocaleString()} products`;
          }
          return `${data.nbHits.toLocaleString()} results${_lastOriginalQuery ? ' for "' + escapeHtml(_lastOriginalQuery) + '"' : ''}`;
        },
      },
    })
  );

  // Hits
  widgets.push(
    instantsearch.widgets.hits({
      container: '#typesense-hits',
      cssClasses: {
        root: 'ts-hits',
        list: 'ts-hits__list',
        item: 'ts-hits__item',
      },
      templates: {
        item(hit) {
          return renderProductCard(hit);
        },
        empty(results) {
          const query = results.query;
          const noResultsHtml = `<div class="ts-no-results">
            <p>No results found${query ? ' for "<strong>' + escapeHtml(query) + '</strong>"' : ''}.</p>
            <p class="text-sm text-theme-light mt-2">Try adjusting your search or filter criteria.</p>
          </div>`;

          if (!query) return noResultsHtml;

          // Render empty state immediately, then try to find a "Did you mean" suggestion
          const containerId = 'ts-dym-' + Date.now();
          const wrapper = `<div id="${containerId}">${noResultsHtml}</div>`;

          const proto = CONFIG.protocol || 'https';
          const port  = CONFIG.port || '443';
          const url   = `${proto}://${CONFIG.host}:${port}/multi_search`;

          fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type':          'application/json',
              'X-TYPESENSE-API-KEY':   CONFIG.apiKey,
            },
            body: JSON.stringify({
              searches: [{
                collection:            collectionName,
                q:                     query,
                query_by:              'title,vendor,tags',
                num_typos:             '2',
                drop_tokens_threshold: '0',
                per_page:              1,
              }],
            }),
          })
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            const hit = data && data.results && data.results[0] && data.results[0].hits && data.results[0].hits[0];
            if (!hit) return;
            // Extract the best highlighted token as the suggestion
            const highlights = hit.highlights || [];
            const titleHighlight = highlights.find(h => h.field === 'title');
            const suggestion = (titleHighlight && titleHighlight.snippet)
              ? titleHighlight.snippet.replace(/<[^>]+>/g, '')  // strip <mark> tags
              : (hit.document && hit.document.title);
            if (!suggestion || suggestion.toLowerCase() === query.toLowerCase()) return;
            const el = document.getElementById(containerId);
            if (!el) return;
            const safeQ   = encodeURIComponent(suggestion);
            const safeTxt = escapeHtml(suggestion);
            el.innerHTML = `<div class="ts-no-results">
              <p>No results found${query ? ' for "<strong>' + escapeHtml(query) + '</strong>"' : ''}.</p>
              <p class="ts-did-you-mean">Did you mean: <a href="/search?q=${safeQ}">${safeTxt}</a>?</p>
              <p class="text-sm text-theme-light mt-2">Try adjusting your search or filter criteria.</p>
            </div>`;
          })
          .catch(() => { /* silent – suggestion is best-effort */ });

          return wrapper;
        },
      },
    })
  );

  // Pagination
  widgets.push(
    instantsearch.widgets.pagination({
      container: '#typesense-pagination',
      cssClasses: {
        root: 'ts-pagination',
        list: 'ts-pagination__list',
        item: 'ts-pagination__item',
        selectedItem: 'ts-pagination__item--selected',
        disabledItem: 'ts-pagination__item--disabled',
        link: 'ts-pagination__link',
      },
      padding: 2,
      showFirst: true,
      showLast: true,
      showPrevious: true,
      showNext: true,
    })
  );

  // Sort by — Best Selling leads on both collection pages and /search since it
  // is the default sort everywhere. Best Match stays available for shoppers who
  // want /search results ranked purely by relevance to what they typed.
  const _sortItems = [
    { label: 'Best Selling', value: collectionName + '/sort/popularity:desc' },
    { label: 'Best Match',   value: collectionName + '/sort/_text_match:desc' },
    { label: 'A-Z', value: collectionName + '/sort/title:asc' },
    { label: 'Z-A', value: collectionName + '/sort/title:desc' },
    { label: 'Price: Low to High', value: collectionName + '/sort/' + (isTrade ? 'trade_price' : 'consumer_price') + ':asc' },
    { label: 'Price: High to Low', value: collectionName + '/sort/' + (isTrade ? 'trade_price' : 'consumer_price') + ':desc' },
    { label: 'Newest', value: collectionName + '/sort/published_at:desc' },
  ];
  widgets.push(
    instantsearch.widgets.sortBy({
      container: '#typesense-sort',
      items: _sortItems,
      cssClasses: {
        root: 'ts-sort',
        select: 'ts-sort__select',
      },
    })
  );

  // ── Facet Widgets (sidebar) ───────────────────────────────────────
  if (sidebarEl) {
    // Helper: wrap a refinementList in a Panel that auto-hides when the
    // facet has fewer than 2 selectable values (so we don't show a panel
    // with only one option or none).
    function facetPanel(header) {
      return instantsearch.widgets.panel({
        templates: { header: () => header },
        cssClasses: { root: 'ts-panel', header: 'ts-panel__header', body: 'ts-panel__body' },
        collapsed: () => false,
        hidden: function (options) {
          // refinementList exposes `items`; hide if empty or single-option
          // (and that option isn't currently refined — keep visible so the
          // user can deselect).
          var items = options && options.items;
          if (!items || items.length === 0) return true;
          if (items.length === 1 && !items[0].isRefined) return true;
          return false;
        },
      });
    }

    // Product type
    if (document.getElementById('typesense-facet-type')) {
      widgets.push(
        facetPanel('Product Types')(instantsearch.widgets.refinementList)({
          container: '#typesense-facet-type',
          attribute: 'tag_type',
          limit: 8,
          showMore: true,
          showMoreLimit: 30,
          sortBy: ['name:asc'],
          cssClasses: {
            root: 'ts-facet',
            list: 'ts-facet__list',
            item: 'ts-facet__item',
            label: 'ts-facet__label',
            checkbox: 'ts-facet__checkbox',
            count: 'ts-facet__count',
            showMore: 'ts-facet__show-more',
            noRefinementRoot: 'ts-facet--no-show-more',
          },
        })
      );
    }

    // "Browse by category" (hierarchical type→subtype menu) was removed — it
    // duplicated the flat "Product Types" list at the top level, and subtype
    // drill-down is already covered by the separate "Subtypes" facet.

    // Subtype (only meaningful when one product type is selected, but it
    // still renders sensibly across all results because the facet itself
    // hides when there are <2 distinct values).
    if (document.getElementById('typesense-facet-subtype')) {
      widgets.push(
        facetPanel('Subtypes')(instantsearch.widgets.refinementList)({
          container: '#typesense-facet-subtype',
          attribute: 'tag_subtype',
          limit: 10,
          showMore: true,
          showMoreLimit: 50,
          sortBy: ['name:asc'],
          cssClasses: {
            root: 'ts-facet',
            list: 'ts-facet__list',
            item: 'ts-facet__item',
            label: 'ts-facet__label',
            checkbox: 'ts-facet__checkbox',
            count: 'ts-facet__count',
            showMore: 'ts-facet__show-more',
            noRefinementRoot: 'ts-facet--no-show-more',
          },
        })
      );
    }

    // Pattern facet (was indexed but never wired; common request from
    // wallpaper/fabric customers).
    if (document.getElementById('typesense-facet-pattern')) {
      widgets.push(
        facetPanel('Patterns')(instantsearch.widgets.refinementList)({
          container: '#typesense-facet-pattern',
          attribute: 'tag_pattern',
          limit: 10,
          showMore: true,
          showMoreLimit: 50,
          sortBy: ['name:asc'],
          cssClasses: {
            root: 'ts-facet',
            list: 'ts-facet__list',
            item: 'ts-facet__item',
            label: 'ts-facet__label',
            checkbox: 'ts-facet__checkbox',
            count: 'ts-facet__count',
            showMore: 'ts-facet__show-more',
            noRefinementRoot: 'ts-facet--no-show-more',
          },
        })
      );
    }

    // Brand
    if (document.getElementById('typesense-facet-brand')) {
      widgets.push(
        facetPanel('Brands')(instantsearch.widgets.refinementList)({
          container: '#typesense-facet-brand',
          attribute: 'tag_brand',
          limit: 8,
          showMore: true,
          showMoreLimit: 50,
          sortBy: ['name:asc'],
          cssClasses: {
            root: 'ts-facet',
            list: 'ts-facet__list',
            item: 'ts-facet__item',
            label: 'ts-facet__label',
            checkbox: 'ts-facet__checkbox',
            count: 'ts-facet__count',
            showMore: 'ts-facet__show-more',
            noRefinementRoot: 'ts-facet--no-show-more',
          },
        })
      );
    }

    // Size (supports rugs and bedding use-cases)
    if (document.getElementById('typesense-facet-size')) {
      widgets.push(
        facetPanel('Sizes')(instantsearch.widgets.refinementList)({
          container: '#typesense-facet-size',
          attribute: 'tag_size',
          limit: 20,
          showMore: true,
          showMoreLimit: 200,
          // Natural numeric sort so "2' x 3'" comes before "10' x 14'".
          // Extracts all numbers from the label and compares them in order,
          // with refined/selected items always pinned to the top.
          sortBy: function(a, b) {
            if (a.isRefined !== b.isRefined) return a.isRefined ? -1 : 1;
            var an = (a.name || '').match(/\d+(?:\.\d+)?/g) || ['0'];
            var bn = (b.name || '').match(/\d+(?:\.\d+)?/g) || ['0'];
            for (var i = 0; i < Math.max(an.length, bn.length); i++) {
              var d = parseFloat(an[i] || 0) - parseFloat(bn[i] || 0);
              if (d !== 0) return d;
            }
            return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
          },
          cssClasses: {
            root: 'ts-facet',
            list: 'ts-facet__list',
            item: 'ts-facet__item',
            label: 'ts-facet__label',
            checkbox: 'ts-facet__checkbox',
            count: 'ts-facet__count',
            showMore: 'ts-facet__show-more',
            noRefinementRoot: 'ts-facet--no-show-more',
          },
        })
      );
    }

    // Height (mostly furniture/lighting). Auto-hides via panel when the
    // result set has no Height: tag values.
    if (document.getElementById('typesense-facet-height')) {
      widgets.push(
        facetPanel('Heights')(instantsearch.widgets.refinementList)({
          container: '#typesense-facet-height',
          attribute: 'tag_height',
          limit: 15,
          showMore: true,
          showMoreLimit: 100,
          sortBy: function(a, b) {
            if (a.isRefined !== b.isRefined) return a.isRefined ? -1 : 1;
            var an = (a.name || '').match(/\d+(?:\.\d+)?/g) || ['0'];
            var bn = (b.name || '').match(/\d+(?:\.\d+)?/g) || ['0'];
            for (var i = 0; i < Math.max(an.length, bn.length); i++) {
              var d = parseFloat(an[i] || 0) - parseFloat(bn[i] || 0);
              if (d !== 0) return d;
            }
            return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
          },
          cssClasses: {
            root: 'ts-facet',
            list: 'ts-facet__list',
            item: 'ts-facet__item',
            label: 'ts-facet__label',
            checkbox: 'ts-facet__checkbox',
            count: 'ts-facet__count',
            showMore: 'ts-facet__show-more',
          },
        })
      );
    }

    // Width (trim, fabric repeats). Auto-hides via panel when no values.
    if (document.getElementById('typesense-facet-width')) {
      widgets.push(
        facetPanel('Widths')(instantsearch.widgets.refinementList)({
          container: '#typesense-facet-width',
          attribute: 'tag_width',
          limit: 15,
          showMore: true,
          showMoreLimit: 100,
          sortBy: function(a, b) {
            if (a.isRefined !== b.isRefined) return a.isRefined ? -1 : 1;
            var an = (a.name || '').match(/\d+(?:\.\d+)?/g) || ['0'];
            var bn = (b.name || '').match(/\d+(?:\.\d+)?/g) || ['0'];
            for (var i = 0; i < Math.max(an.length, bn.length); i++) {
              var d = parseFloat(an[i] || 0) - parseFloat(bn[i] || 0);
              if (d !== 0) return d;
            }
            return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
          },
          cssClasses: {
            root: 'ts-facet',
            list: 'ts-facet__list',
            item: 'ts-facet__item',
            label: 'ts-facet__label',
            checkbox: 'ts-facet__checkbox',
            count: 'ts-facet__count',
            showMore: 'ts-facet__show-more',
          },
        })
      );
    }

    // Shape
    if (document.getElementById('typesense-facet-shape')) {
      widgets.push(
        facetPanel('Shapes')(instantsearch.widgets.refinementList)({
          container: '#typesense-facet-shape',
          attribute: 'tag_shape',
          limit: 10,
          showMore: true,
          showMoreLimit: 30,
          sortBy: ['name:asc'],
          cssClasses: {
            root: 'ts-facet',
            list: 'ts-facet__list',
            item: 'ts-facet__item',
            label: 'ts-facet__label',
            checkbox: 'ts-facet__checkbox',
            count: 'ts-facet__count',
            showMore: 'ts-facet__show-more',
          },
        })
      );
    }

    // Color
    if (document.getElementById('typesense-facet-color')) {
      widgets.push(
        facetPanel('Colors')(instantsearch.widgets.refinementList)({
          container: '#typesense-facet-color',
          attribute: 'tag_color',
          limit: 10,
          showMore: true,
          showMoreLimit: 30,
          sortBy: ['name:asc'],
          cssClasses: {
            root: 'ts-facet',
            list: 'ts-facet__list',
            item: 'ts-facet__item',
            label: 'ts-facet__label',
            checkbox: 'ts-facet__checkbox',
            count: 'ts-facet__count',
            showMore: 'ts-facet__show-more',
          },
        })
      );
    }

    // Construction
    if (document.getElementById('typesense-facet-construction')) {
      widgets.push(
        facetPanel('Construction')(instantsearch.widgets.refinementList)({
          container: '#typesense-facet-construction',
          attribute: 'tag_construction',
          limit: 10,
          showMore: true,
          showMoreLimit: 30,
          sortBy: ['name:asc'],
          cssClasses: {
            root: 'ts-facet',
            list: 'ts-facet__list',
            item: 'ts-facet__item',
            label: 'ts-facet__label',
            checkbox: 'ts-facet__checkbox',
            count: 'ts-facet__count',
            showMore: 'ts-facet__show-more',
          },
        })
      );
    }

    // Content
    if (document.getElementById('typesense-facet-content')) {
      widgets.push(
        facetPanel('Content')(instantsearch.widgets.refinementList)({
          container: '#typesense-facet-content',
          attribute: 'tag_content',
          limit: 10,
          showMore: true,
          showMoreLimit: 30,
          sortBy: ['name:asc'],
          cssClasses: {
            root: 'ts-facet',
            list: 'ts-facet__list',
            item: 'ts-facet__item',
            label: 'ts-facet__label',
            checkbox: 'ts-facet__checkbox',
            count: 'ts-facet__count',
            showMore: 'ts-facet__show-more',
          },
        })
      );
    }

    // Designer
    if (document.getElementById('typesense-facet-designer')) {
      widgets.push(
        facetPanel('Designers')(instantsearch.widgets.refinementList)({
          container: '#typesense-facet-designer',
          attribute: 'tag_designer',
          limit: 10,
          showMore: true,
          showMoreLimit: 50,
          sortBy: ['name:asc'],
          cssClasses: {
            root: 'ts-facet',
            list: 'ts-facet__list',
            item: 'ts-facet__item',
            label: 'ts-facet__label',
            checkbox: 'ts-facet__checkbox',
            count: 'ts-facet__count',
            showMore: 'ts-facet__show-more',
          },
        })
      );
    }



    // Style
    if (document.getElementById('typesense-facet-style')) {
      widgets.push(
        facetPanel('Styles')(instantsearch.widgets.refinementList)({
          container: '#typesense-facet-style',
          attribute: 'tag_style',
          limit: 10,
          showMore: true,
          showMoreLimit: 30,
          sortBy: ['name:asc'],
          cssClasses: {
            root: 'ts-facet',
            list: 'ts-facet__list',
            item: 'ts-facet__item',
            label: 'ts-facet__label',
            checkbox: 'ts-facet__checkbox',
            count: 'ts-facet__count',
            showMore: 'ts-facet__show-more',
          },
        })
      );
    }

    // Category (custom hierarchical render)
    //
    // `tag_category` is a flat facet whose values encode a one-level hierarchy
    // with ">", e.g. "Animals", "Animals>Cat", "Animals>Dog". A plain
    // refinementList renders every value as its own inline row, so a parent
    // with many children (Animals has ~20) floods the list and pushes the rest
    // of the alphabet past the show-more cap. Instead we group children under
    // their parent and reveal them from a collapsible dropdown next to the
    // parent, keeping the top-level list to one row per category.
    if (document.getElementById('typesense-facet-category')) {
      const CAT_INITIAL_GROUPS = 12;

      function escapeHtml(str) {
        return String(str).replace(/[&<>"']/g, function (c) {
          return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
      }

      function catCheckboxRow(item, labelText) {
        return (
          '<label class="ts-facet__label">' +
            '<input type="checkbox" class="ts-facet__checkbox" data-cat-value="' +
              escapeHtml(item.value) + '"' + (item.isRefined ? ' checked' : '') + '>' +
            '<span class="ts-cat__name">' + escapeHtml(labelText) + '</span>' +
            '<span class="ts-facet__count">' + item.count + '</span>' +
          '</label>'
        );
      }

      function renderCategory(renderOptions, isFirstRender) {
        const { items, refine, widgetParams } = renderOptions;
        const container = typeof widgetParams.container === 'string'
          ? document.querySelector(widgetParams.container)
          : widgetParams.container;
        if (!container) return;

        container.__catItems = items;
        container.__catRefine = refine;

        if (isFirstRender) {
          container.__catExpanded = new Set();
          container.__catShowAll = false;

          container.__catBuild = function build() {
            const list = container.__catItems || [];
            const refineFn = container.__catRefine;
            const expanded = container.__catExpanded;

            // Group flat facet values into parent → children.
            const groups = new Map();
            function group(name) {
              if (!groups.has(name)) groups.set(name, { name: name, self: null, children: [] });
              return groups.get(name);
            }
            list.forEach(function (it) {
              const sep = it.label.indexOf('>');
              if (sep === -1) {
                group(it.label).self = it;
              } else {
                const parent = it.label.slice(0, sep).trim();
                const child = it.label.slice(sep + 1).trim();
                group(parent).children.push(Object.assign({ childName: child }, it));
              }
            });

            const ordered = Array.from(groups.values()).sort(function (a, b) {
              return a.name.localeCompare(b.name);
            });
            ordered.forEach(function (g) {
              g.children.sort(function (a, b) { return a.childName.localeCompare(b.childName); });
              // Keep any group with an active child open so the refinement stays visible.
              if (g.children.some(function (c) { return c.isRefined; })) expanded.add(g.name);
            });

            const showAll = container.__catShowAll;
            const visible = showAll ? ordered : ordered.slice(0, CAT_INITIAL_GROUPS);

            let html = '<ul class="ts-facet__list">';
            visible.forEach(function (g) {
              const hasChildren = g.children.length > 0;
              const isOpen = expanded.has(g.name);
              html += '<li class="ts-facet__item ts-cat__group">';
              html += '<div class="ts-cat__row">';
              if (g.self) {
                html += catCheckboxRow(g.self, g.name);
              } else {
                // Parent has children but was never applied on its own — render
                // the name as a plain toggle header.
                html += '<span class="ts-facet__label ts-cat__parent-label">' +
                  '<span class="ts-cat__name">' + escapeHtml(g.name) + '</span></span>';
              }
              if (hasChildren) {
                html += '<button type="button" class="ts-cat__toggle' +
                  (isOpen ? ' ts-cat__toggle--open' : '') + '" data-cat-parent="' +
                  escapeHtml(g.name) + '" aria-expanded="' + (isOpen ? 'true' : 'false') +
                  '" aria-label="Toggle ' + escapeHtml(g.name) + ' subcategories">' +
                  '<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">' +
                  '<path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" ' +
                  'stroke-linecap="round" stroke-linejoin="round"/></svg></button>';
              }
              html += '</div>';
              if (hasChildren && isOpen) {
                html += '<ul class="ts-facet__list ts-cat__children">';
                g.children.forEach(function (c) {
                  html += '<li class="ts-facet__item">' + catCheckboxRow(c, c.childName) + '</li>';
                });
                html += '</ul>';
              }
              html += '</li>';
            });
            html += '</ul>';

            if (ordered.length > CAT_INITIAL_GROUPS) {
              html += '<button type="button" class="ts-facet__show-more" data-cat-showmore>' +
                (showAll ? 'Show less' : 'Show more') + '</button>';
            }

            container.innerHTML = html;
          };

          // Delegated handlers (attached once; DOM is rebuilt each render).
          container.addEventListener('change', function (e) {
            const cb = e.target.closest('[data-cat-value]');
            if (!cb) return;
            container.__catRefine(cb.getAttribute('data-cat-value'));
          });
          container.addEventListener('click', function (e) {
            const toggle = e.target.closest('[data-cat-parent]');
            if (toggle) {
              const name = toggle.getAttribute('data-cat-parent');
              if (container.__catExpanded.has(name)) container.__catExpanded.delete(name);
              else container.__catExpanded.add(name);
              container.__catBuild();
              return;
            }
            const more = e.target.closest('[data-cat-showmore]');
            if (more) {
              container.__catShowAll = !container.__catShowAll;
              container.__catBuild();
            }
          });
        }

        container.__catBuild();
      }

      const makeCategoryList = instantsearch.connectors.connectRefinementList(renderCategory);

      widgets.push(
        facetPanel('Categories')(makeCategoryList)({
          container: '#typesense-facet-category',
          attribute: 'tag_category',
          limit: 1000,
          sortBy: ['name:asc'],
        })
      );
    }

    // Room
    if (document.getElementById('typesense-facet-room')) {
      widgets.push(
        facetPanel('Rooms')(instantsearch.widgets.refinementList)({
          container: '#typesense-facet-room',
          attribute: 'tag_room',
          limit: 10,
          showMore: true,
          showMoreLimit: 30,
          sortBy: ['name:asc'],
          cssClasses: {
            root: 'ts-facet',
            list: 'ts-facet__list',
            item: 'ts-facet__item',
            label: 'ts-facet__label',
            checkbox: 'ts-facet__checkbox',
            count: 'ts-facet__count',
            showMore: 'ts-facet__show-more',
          },
        })
      );
    }

    // Price range slider (custom connector)
    const makePriceSlider = instantsearch.connectors.connectRange(
      function renderPriceSlider(renderOptions, isFirstRender) {
        const { start, range, refine, widgetParams } = renderOptions;
        const container = typeof widgetParams.container === 'string'
          ? document.querySelector(widgetParams.container)
          : widgetParams.container;
        if (!container) return;

        const min = range.min != null ? range.min : 0;
        const max = range.max != null ? range.max : 1000;
        const curMin = start[0] !== -Infinity && start[0] != null ? start[0] : min;
        const curMax = start[1] !== Infinity && start[1] != null ? start[1] : max;

        if (isFirstRender) {
          container.innerHTML = '<div class="ts-range-slider">' +
            '<div class="ts-range-slider__labels">' +
              '<span class="ts-range-slider__val ts-range-slider__val--min"></span>' +
              '<span class="ts-range-slider__val ts-range-slider__val--max"></span>' +
            '</div>' +
            '<div class="ts-range-slider__track">' +
              '<div class="ts-range-slider__fill"></div>' +
              '<input type="range" class="ts-range-slider__input ts-range-slider__input--min" step="1">' +
              '<input type="range" class="ts-range-slider__input ts-range-slider__input--max" step="1">' +
            '</div>' +
          '</div>';

          const inputMin = container.querySelector('.ts-range-slider__input--min');
          const inputMax = container.querySelector('.ts-range-slider__input--max');

          function onSlide() {
            let lo = Number(inputMin.value);
            let hi = Number(inputMax.value);
            if (lo > hi) { const t = lo; lo = hi; hi = t; }
            updateUI(container, lo, hi, Number(inputMin.min), Number(inputMin.max));
          }

          function onCommit() {
            let lo = Number(inputMin.value);
            let hi = Number(inputMax.value);
            if (lo > hi) { const t = lo; lo = hi; hi = t; }
            refine([lo, hi]);
          }

          inputMin.addEventListener('input', onSlide);
          inputMax.addEventListener('input', onSlide);
          inputMin.addEventListener('change', onCommit);
          inputMax.addEventListener('change', onCommit);
          container.__refine = refine;
        }

        const inputMin = container.querySelector('.ts-range-slider__input--min');
        const inputMax = container.querySelector('.ts-range-slider__input--max');
        inputMin.min = inputMax.min = min;
        inputMin.max = inputMax.max = max;
        inputMin.value = curMin;
        inputMax.value = curMax;
        container.__refine = refine;
        updateUI(container, curMin, curMax, min, max);
      }
    );

    function updateUI(container, lo, hi, min, max) {
      const valMin = container.querySelector('.ts-range-slider__val--min');
      const valMax = container.querySelector('.ts-range-slider__val--max');
      const fill = container.querySelector('.ts-range-slider__fill');
      const safeMin = Number.isFinite(min) ? min : 0;
      const safeMax = Number.isFinite(max) ? max : safeMin;
      const clampedLo = Math.min(Math.max(Number(lo), safeMin), safeMax);
      const clampedHi = Math.min(Math.max(Number(hi), safeMin), safeMax);
      const total = safeMax - safeMin || 1;
      const leftPct = Math.max(0, Math.min(100, ((clampedLo - safeMin) / total) * 100));
      const rightPct = Math.max(0, Math.min(100, ((safeMax - clampedHi) / total) * 100));
      valMin.textContent = formatMoney(clampedLo);
      valMax.textContent = formatMoney(clampedHi);
      fill.style.left = leftPct + '%';
      fill.style.right = rightPct + '%';
    }

    widgets.push(
      instantsearch.widgets.panel({
        templates: { header: () => 'Price' },
        cssClasses: { root: 'ts-panel', header: 'ts-panel__header', body: 'ts-panel__body' },
        collapsed: () => false,
        hidden: function (options) {
          // Hide if there's no usable range (no items or min===max).
          var range = options && options.range;
          if (!range) return true;
          if (range.min == null || range.max == null) return true;
          if (range.min === range.max) return true;
          return false;
        },
      })(makePriceSlider)({
        container: '#typesense-facet-price',
        attribute: priceAttr,
      })
    );

    // Sale toggle. Uses the auto-derived `on_sale` boolean indexed by
    // transform.py (compare_at_price > price), not a merchant-curated tag,
    // so it stays accurate even when sales aren't tagged. The panel hides
    // itself when no sale items exist in the current result set.
    widgets.push(
      instantsearch.widgets.panel({
        cssClasses: { root: 'ts-panel ts-panel--toggle', body: 'ts-panel__body' },
        hidden: function (options) {
          if (!options) return true;
          if (options.canRefine === false) return true;
          if (options.value && options.value.count === 0 && !options.value.isRefined) return true;
          return false;
        },
      })(instantsearch.widgets.toggleRefinement)({
        container: '#typesense-facet-sale',
        attribute: 'on_sale',
        on: true,
        templates: {
          labelText: () => 'On Sale',
        },
        cssClasses: {
          root: 'ts-toggle',
          label: 'ts-toggle__label',
          checkbox: 'ts-toggle__checkbox',
        },
      })
    );

    // Current refinements (active filter pills)
    widgets.push(
      instantsearch.widgets.currentRefinements({
        container: '#typesense-current-refinements',
        cssClasses: {
          root: 'ts-refinements',
          list: 'ts-refinements__list',
          item: 'ts-refinements__item',
          label: 'ts-refinements__label',
          category: 'ts-refinements__category',
          delete: 'ts-refinements__delete',
        },
        transformItems(items) {
          return items.map((item) => {
            // Prettify attribute names
            const labelMap = {
              tag_brand: 'Brand',
              tag_designer: 'Designer',
              tag_type: 'Type',
              tag_color: 'Color',
              tag_construction: 'Construction',
              tag_content: 'Content',
              tag_pattern: 'Pattern',
              tag_style: 'Style',
              tag_category: 'Category',
              tag_room: 'Room',
              tag_shape: 'Shape',
              tag_size: 'Size',
              tag_height: 'Height',
              tag_width: 'Width',
              tag_group: 'Group',
              on_sale: 'Sale',
              consumer_price: 'Price',
              trade_price: 'Price',
            };
            return { ...item, label: labelMap[item.attribute] || item.attribute };
          });
        },
      })
    );

    // Clear refinements
    widgets.push(
      instantsearch.widgets.clearRefinements({
        container: '#typesense-clear-filters',
        cssClasses: {
          root: 'ts-clear',
          button: 'ts-clear__button',
          disabledButton: 'ts-clear__button--disabled',
        },
        templates: {
          resetLabel: () => 'Clear all filters',
        },
      })
    );
  }

  // ── Configure: hitsPerPage + optional collection filter ───────────
  // IMPORTANT: only ONE configure() widget is safe per InstantSearch
  // instance. The Typesense adapter merges multiple configure widgets
  // last-write-wins on overlapping keys, which was silently discarding
  // the collection `filters` and causing collection pages to return all
  // products instead of only those in the collection. Merged here.
  {
    const configureParams = { hitsPerPage: 48 };
    const SALE_PAGES_WITHOUT_OUTLET = ['prime-sale'];
    if (pageType === 'collection' && collectionHandle) {
      configureParams.filters = 'collections:=`' + collectionHandle + '`';
      // Sale landing pages list only what the sale code applies to. Outlet is
      // already marked down and the code skips it (Barbara 2026-10-05), but a
      // brand-based collection can't leave it out on Shopify's side.
      if (SALE_PAGES_WITHOUT_OUTLET.includes(collectionHandle)) {
        configureParams.filters += ' && tag_group:!=`Outlet`';
      }
    }
    widgets.push(instantsearch.widgets.configure(configureParams));
  }

  // ── Add all widgets and start ─────────────────────────────────────
  search.addWidgets(widgets);

  // Error handling — show friendly message if Typesense is unavailable
  search.on('error', function (e) {
    var msg = (e && e.error && e.error.message) || '';
    console.warn('[Typesense Search] Error:', msg);
    var hitsEl = document.getElementById('typesense-hits');
    var loadingEl = document.getElementById('typesense-loading');
    if (loadingEl) loadingEl.style.display = 'none';
    if (hitsEl && !hitsEl.dataset.errorShown) {
      hitsEl.dataset.errorShown = '1';
      hitsEl.innerHTML =
        '<div class="ts-no-results">' +
        '<p>Search is temporarily unavailable. Please try again shortly.</p>' +
        '<p class="text-sm text-theme-light mt-2">Our search index is being built.</p>' +
        '</div>';
    }
  });

  // Hide loading indicator once first render completes; also re-emit
  // the legacy `on:facet-filters:updated` event so compare-drawer.js (the
  // only remaining listener) keeps syncing its checkbox state when the
  // hits grid is re-rendered after a refinement change.
  var _lastLoggedQuery = '__init__';
  search.on('render', function () {
    var loadingEl = document.getElementById('typesense-loading');
    if (loadingEl) loadingEl.style.display = 'none';
    document.dispatchEvent(
      new CustomEvent('on:facet-filters:updated', { bubbles: true })
    );
    // The async ideal.house SDK observes `.grid-view-item`, so newly rendered
    // Typesense cards are picked up without a manual re-initialization call.

    // Analytics — log distinct search-page queries to the admin backend.
    //
    // Important: `render` fires multiple times per search lifecycle. The
    // first render after a query change happens BEFORE results arrive
    // (lastResults is null or still holds the previous query's data). If
    // we logged then, every search would post `result_count: 0` and our
    // Zero-Result Queries report would be polluted with queries that
    // actually returned plenty. So we only post when:
    //   1. lastResults exists, AND
    //   2. lastResults.query matches the current state.query
    //      (i.e. the visible results actually correspond to this query)
    // Then we mark _lastLoggedQuery so the next render doesn't re-log.
    try {
      var adminHost = (CONFIG.adminHost || '').replace(/\/+$/, '');
      var q = (search.helper && search.helper.state && search.helper.state.query) || '';
      var lastRes = search.helper && search.helper.lastResults;
      var resultsMatchQuery = lastRes && lastRes.query === q;
      if (adminHost && q && resultsMatchQuery && q !== _lastLoggedQuery && pageType === 'search') {
        _lastLoggedQuery = q;
        var nbHits = lastRes.nbHits || 0;
        fetch('https://' + adminHost + '/search-log', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ q: q, result_count: nbHits, source: 'search_page' }),
          keepalive: true,
          mode:    'cors',
        }).catch(function () { /* lossy */ });
      }
    } catch (_) { /* never break search */ }
  });

  search.start();

  // ── Mobile filter drawer toggle ───────────────────────────────────
  // The sidebar is hidden on mobile via CSS; this opens it as a
  // drawer/overlay. Two openers (the in-toolbar button) and three
  // dismissers (the X button, ESC, clicking outside) keep the trap
  // small so a busy template doesn't strand the user inside the panel.
  (function initFilterToggle() {
    var sidebar = document.getElementById('typesense-sidebar');
    var openBtn = document.getElementById('typesense-filter-toggle');
    var closeBtn = document.getElementById('typesense-filter-close');
    if (!sidebar || !openBtn) return;

    var OPEN_CLASS = 'ts-sidebar--open';
    var BODY_LOCK_CLASS = 'ts-sidebar-open-body';

    function open() {
      sidebar.classList.add(OPEN_CLASS);
      document.body.classList.add(BODY_LOCK_CLASS);
      openBtn.setAttribute('aria-expanded', 'true');
    }
    function close() {
      sidebar.classList.remove(OPEN_CLASS);
      document.body.classList.remove(BODY_LOCK_CLASS);
      openBtn.setAttribute('aria-expanded', 'false');
    }

    openBtn.addEventListener('click', function (e) {
      e.preventDefault();
      if (sidebar.classList.contains(OPEN_CLASS)) close(); else open();
    });
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        close();
      });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && sidebar.classList.contains(OPEN_CLASS)) close();
    });
    // Click anywhere outside the drawer (and not on the toggle that
    // opened it) to close. Use `pointerdown` so the close fires before
    // any incidental click handler inside the page chrome runs. The CSS
    // `::before` backdrop is purely visual; pseudo-elements can't catch
    // their own clicks, so this listener is what actually dismisses on
    // tap-outside.
    document.addEventListener('pointerdown', function (e) {
      if (!sidebar.classList.contains(OPEN_CLASS)) return;
      if (sidebar.contains(e.target)) return;
      if (openBtn.contains(e.target)) return;
      close();
    });
    // Close when the viewport grows back to desktop so the body lock
    // doesn't persist after a user rotates / resizes.
    var mql = window.matchMedia('(min-width: 1024px)');
    var onChange = function (ev) { if (ev.matches) close(); };
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else if (mql.addListener) mql.addListener(onChange);
  })();

  // ── Facet panel collapse toggle ───────────────────────────────────
  // The InstantSearch `panel` widget owns the `.ais-Panel--collapsed`
  // class and rewrites it on every render, so we use our own class
  // (`.ts-panel--collapsed`) that survives re-renders. Sidebar header
  // clicks toggle it; CSS hides the body and rotates the arrow.
  if (sidebarEl) {
    sidebarEl.addEventListener('click', function (e) {
      const header = e.target.closest('.ts-panel__header');
      if (!header || !sidebarEl.contains(header)) return;
      // Don't swallow clicks on real controls inside the header
      // (e.g. InstantSearch's hidden collapse button).
      if (e.target.closest('button, a, input')) return;
      const panel = header.closest('.ts-panel, .ais-Panel');
      if (panel) panel.classList.toggle('ts-panel--collapsed');
    });
  }

  // ── Expose for predictive search integration ──────────────────────
  window.__typesenseSearch = search;
  window.__typesenseSearchClient = searchClient;
  window.__typesenseCollectionName = collectionName;
})();
